/* eslint-disable react/no-multi-comp */
import React from 'react';
import {applyRound} from '@ey/utils';
import {actionIcInfoOutline24px} from '@ey/motif-react-wrapper/assets/icons/index';
import styled from 'styled-components';
import {isNil, round} from 'lodash';
import {materialityTypes} from '../../../../../util/uiconstants';
import {currentResource, isNullOrEmpty, isNullOrUndefined, labels} from '../../../../../util/utils';
import Icon from '../../../BaseComponentWrappers/MotifIcon';
import NumberFormat from '../../../../Common/NumberFormat/NumberFormat';

const getSubString = (str, startStr, endStr) => {
	const startSeparatorLength = startStr.length;
	return str.substring(str.indexOf(startStr) + startSeparatorLength, str.lastIndexOf(endStr));
};

const getSuggestedPMBracketLabel = (suggestedPMBracket) => {
	if (suggestedPMBracket === 1) {
		return labels.lowerEnd;
	}
	if (suggestedPMBracket === 2) {
		return labels.middle;
	}
	if (suggestedPMBracket === 3) {
		return labels.higherEnd;
	}
	if (suggestedPMBracket === 4) {
		return labels.lowEnd;
	}
};

const getMaterialityLabel = (materialityTypeId) => {
	let materialityType = currentResource.materialityTypes.find(
		(materialityType) => materialityType.value === materialityTypeId
	);

	return materialityType?.label;
};

// bufferType = 'high' | 'low'
export function calculateBufferedValue(basisPercentage, basisAmount, bufferType, bufferPercentage = 0.05) {
	if (!basisPercentage || !basisAmount) {
		return 0;
	}

	const inputValue = basisAmount * basisPercentage;
	const bufferMultiplier = bufferType === 'high' ? 1 + bufferPercentage : 1 - bufferPercentage;

	let bufferedValue = inputValue * Math.abs(bufferMultiplier);
	bufferedValue = applyRound(bufferedValue);

	return bufferedValue;
}

const roundSadValue = (value, decimalsAmmount) => {
	return parseFloat(applyRound(value, decimalsAmmount));
};

const MaterialityDisplay = {
	MeasurementBasis: ({materialityTypeID, summary, ...props}) => {
		return materialityTypeID ? (
			<MaterialityInformationLine className="MaterialityInformationLine aresLabelNameValueWrapper" summary={summary}>
				<span className="materiality-information-line__label aresLabelName">{labels.measurementBasis}:</span>
				<span className="materiality-information-line__value aresLabelValue">
					{getMaterialityLabel(materialityTypeID)}
				</span>
			</MaterialityInformationLine>
		) : null;
	},
	BasisAdjustedAmount: ({basisAmount, summary, ...props}) => {
		return (
			<MaterialityInformationLine className="MaterialityInformationLine aresLabelNameValueWrapper" summary={summary}>
				<span className="materiality-information-line__label aresLabelName">{labels.basisAmount}:</span>
				<span className="materiality-information-line__value aresLabelValue">
					{!isNil(basisAmount) ? <NumberFormat value={basisAmount} renderText={(value) => value} /> : <> {'-'}</>}
				</span>
			</MaterialityInformationLine>
		);
	},
	EndOfRange: ({label, value, percentage}) => {
		return !isNil(label) && !isNil(value) && !isNil(percentage) ? (
			<>
				<span className="materiality-information-line__label aresLabelName">{label}:</span>
				<span className="materiality-information-line__percentage">
					<NumberFormat value={percentage} renderText={(value) => value} suffix="%" />
				</span>
				<span className="materiality-information-line__value aresLabelValue">
					(<NumberFormat value={Math.round(value)} renderText={(value) => value} />)
				</span>
			</>
		) : null;
	},
	MinRange: ({percentage, value}) => {
		return (
			<>
				<span className="materiality-information-line__percentage">
					<NumberFormat value={percentage} renderText={(value) => value} suffix="%" />
				</span>
				<span className="materiality-information-line__value aresLabelValue">
					(<NumberFormat value={Math.round(value)} renderText={(value) => value} />)
				</span>
			</>
		);
	},
	SuggestedLowEndOfRange: ({
		suggestedPMBracket,
		minimumRange,
		maximumRange,
		basisAmount,
		setShowRationaleOnLowValues,
		pmAmount,
		...props
	}) => {
		let lowEndValue = 0;
		if (suggestedPMBracket === 1 || suggestedPMBracket === 4) {
			lowEndValue = minimumRange;
		} else if (suggestedPMBracket === 2) {
			lowEndValue = (maximumRange - minimumRange) * (1 / 3) + minimumRange; // ((MaximumRange - MinimumRange)  x (1 / 3)) +  MinimumRange
		} else if (suggestedPMBracket === 3) {
			lowEndValue = (maximumRange - minimumRange) * (2 / 3) + minimumRange; // ((MaximumRange - MinimumRange)  x (2 / 3)) +  MinimumRange
		}

		const roundedLowEndValue = lowEndValue && round(lowEndValue * 100, 1);
		const calculatedValue = roundedLowEndValue && (roundedLowEndValue / 100) * basisAmount;
		const suggestedBufferLowRange = calculateBufferedValue(lowEndValue, basisAmount, 'low');

		if (setShowRationaleOnLowValues) {
			if (suggestedPMBracket === 4) {
				// Show rationale if the user enters PM percentage or value that is different than the suggested low end of range +- 5% buffer
				const suggestedBufferHighRange = calculateBufferedValue(lowEndValue, basisAmount, 'high');
				const _showRationale = pmAmount < suggestedBufferLowRange || pmAmount > suggestedBufferHighRange;

				setShowRationaleOnLowValues(_showRationale);
			} else {
				setShowRationaleOnLowValues(pmAmount < suggestedBufferLowRange);
			}
		}

		return <MaterialityDisplay.MinRange percentage={roundedLowEndValue} value={calculatedValue} />;
	},
	SuggestedHighEndOfRange: ({
		suggestedPMBracket,
		minimumRange,
		maximumRange,
		basisAmount,
		setShowRationaleOnHighValues,
		pmAmount,
		...props
	}) => {
		let highEndValue = 0;
		if (suggestedPMBracket === 1) {
			highEndValue = (maximumRange - minimumRange) * (1 / 3) + minimumRange; //((MaximumRange - MinimumRange)  x (1 / 3)) +  MinimumRange
		} else if (suggestedPMBracket === 2) {
			highEndValue = (maximumRange - minimumRange) * (2 / 3) + minimumRange; //  ((MaximumRange - MinimumRange)  x (2 / 3)) +  MinimumRange
		} else if (suggestedPMBracket === 3) {
			highEndValue = maximumRange;
		}

		const roundedHighEndValue = highEndValue && round(highEndValue * 100, 1);
		const calculatedValue = roundedHighEndValue && (roundedHighEndValue / 100) * basisAmount;
		const suggestedBufferHighRange = calculateBufferedValue(highEndValue, basisAmount, 'high');
		setShowRationaleOnHighValues && setShowRationaleOnHighValues(pmAmount > suggestedBufferHighRange ? true : false);
		return <MaterialityDisplay.MinRange percentage={roundedHighEndValue} value={calculatedValue} />;
	},

	LowEndOfRange: ({minimumRange, basisAmount, ...props}) => {
		const calculatedValue = minimumRange * basisAmount;

		return (
			<MaterialityDisplay.EndOfRange
				label={labels.lowEndOfRange}
				value={calculatedValue}
				percentage={minimumRange && round(minimumRange * 100, 1)}
			/>
		);
	},
	HighEndOfRange: ({maximumRange, basisAmount, ...props}) => {
		const calculatedValue = maximumRange * basisAmount;

		return (
			<MaterialityDisplay.EndOfRange
				label={labels.highEndOfRange}
				value={calculatedValue}
				percentage={maximumRange && round(maximumRange * 100, 1)}
			/>
		);
	},
	Range: ({maximumRange, minimumRange, basisAmount, summary, ...props}) => {
		return !isNil(basisAmount) && (!isNil(maximumRange) || !isNil(minimumRange)) ? (
			<MaterialityInformationLine className="MaterialityInformationLine" summary={summary}>
				<MaterialityDisplay.LowEndOfRange minimumRange={minimumRange} basisAmount={basisAmount} />
				{!isNil(maximumRange) && !isNil(minimumRange) && <span className="RangeSeparator"> - </span>}
				<MaterialityDisplay.HighEndOfRange maximumRange={maximumRange} basisAmount={basisAmount} />
			</MaterialityInformationLine>
		) : null;
	},
	SuggestedRange: ({
		suggestedPMBracket,
		maximumRange,
		minimumRange,
		basisAmount,
		setShowRationaleOnLowValues,
		setShowRationaleOnHighValues,
		summary,
		pmAmount,
		materialityTypeID,
		...props
	}) => {
		if (
			materialityTypeID === materialityTypes.EarningsBasedOther ||
			materialityTypeID === materialityTypes.CapitalBasedOther ||
			materialityTypeID === materialityTypes.Other
		) {
			if (isNil(basisAmount) || isNil(suggestedPMBracket)) {
				return null;
			}
			if (isNil(maximumRange) || isNil(minimumRange)) {
				return (
					!summary && (
						<>
							<span className="materiality-information-line__label aresLabelName materialityInformationLineWrap">
								{labels.suggestedRange.split('{0}')[0]}
								{getSuggestedPMBracketLabel(suggestedPMBracket)}
								{getSubString(labels.suggestedRange, '{0}', '{1}')}
								{labels.suggestedRange.split('{2}')[1]}
							</span>
						</>
					)
				);
			}
			return null;
		}

		if (!isNil(suggestedPMBracket) && !isNil(basisAmount)) {
			const rangeLabelSplit = labels.suggestedRangeLowPMBracket.split('{0}');

			if (suggestedPMBracket === 4) {
				// On PMBracket 4, only the low end of range is shown.
				// Setting this state to false, so the setShowRationaleOnLowValues setting determines if the rationale will be shown
				setShowRationaleOnHighValues && setShowRationaleOnHighValues(false);

				return (
					<MaterialityInformationLine className="MaterialityInformationLine" summary={summary}>
						<span className="materiality-information-line__label aresLabelName">{rangeLabelSplit[0]}</span>
						<span className="SuggestedRangeWrapper">
							<MaterialityDisplay.SuggestedLowEndOfRange
								suggestedPMBracket={suggestedPMBracket}
								minimumRange={minimumRange}
								maximumRange={maximumRange}
								basisAmount={basisAmount}
								setShowRationaleOnLowValues={setShowRationaleOnLowValues}
								pmAmount={pmAmount}
							/>
						</span>
						<span className="aresLabelName">{rangeLabelSplit[1]}</span>
					</MaterialityInformationLine>
				);
			} else {
				return (
					<MaterialityInformationLine className="MaterialityInformationLine" summary={summary}>
						{!summary && (
							<span className="materiality-information-line__label aresLabelName materialityInformationLineWrap">
								{labels.suggestedRange.split('{0}')[0]}
							</span>
						)}
						{!summary && (
							<span className="materiality-information-line__label aresLabelName materialityInformationLineWrap">
								{getSuggestedPMBracketLabel(suggestedPMBracket)}
							</span>
						)}
						{summary && !isNil(minimumRange) && !isNil(maximumRange) && (
							<span className="materiality-information-line__label aresLabelName materialityInformationLineWrap">
								{labels.suggestedRangeSummary + ':'}
							</span>
						)}
						{!summary && (
							<span className="materiality-information-line__label aresLabelName">
								{getSubString(labels.suggestedRange, '{0}', '{1}')}
							</span>
						)}
						{!isNil(minimumRange) && !isNil(maximumRange) && (
							<span className="SuggestedRangeWrapper">
								<MaterialityDisplay.SuggestedLowEndOfRange
									suggestedPMBracket={suggestedPMBracket}
									minimumRange={minimumRange}
									maximumRange={maximumRange}
									basisAmount={basisAmount}
									setShowRationaleOnLowValues={setShowRationaleOnLowValues}
									pmAmount={pmAmount}
								/>
								<span>{getSubString(labels.suggestedRange, '{1}', '{2}')}</span>
								<MaterialityDisplay.SuggestedHighEndOfRange
									suggestedPMBracket={suggestedPMBracket}
									minimumRange={minimumRange}
									maximumRange={maximumRange}
									basisAmount={basisAmount}
									setShowRationaleOnHighValues={setShowRationaleOnHighValues}
									pmAmount={pmAmount}
								/>
							</span>
						)}
						{!summary && (
							<span className="materiality-information-line__label aresLabelName materialityInformationLinelabelWrap">
								{labels.suggestedRange.split('{2}')[1]}
							</span>
						)}
					</MaterialityInformationLine>
				);
			}
		}
		return null;
	},
	PriorPeriodPM: ({materialityTypeID, pmAmount, summary, ...props}) => {
		return !isNil(pmAmount) ? (
			<MaterialityInformationLine className="MaterialityInformationLine" summary={summary}>
				<span className="materiality-information-line__label aresLabelName">{labels.priorPeriodPm}</span>
				<span className="materiality-information-line__value aresLabelValue">
					<NumberFormat value={pmAmount} renderText={(value) => value} />
				</span>
				{materialityTypeID && (
					<span className="materiality-information-line__value aresLabelValue">
						({getMaterialityLabel(materialityTypeID)})
					</span>
				)}
			</MaterialityInformationLine>
		) : null;
	},
	CurrentPeriodPM: ({basisAmount, pmAmount, summary, maximumRange, ...props}) => {
		const calculatedValue = maximumRange * basisAmount;
		const bufferRange = calculatedValue + 0.05 * calculatedValue;
		let percentageValue;
		if (!isNil(pmAmount) && basisAmount) {
			percentageValue = round((pmAmount / basisAmount) * 100, 1);
		}

		return (
			<MaterialityInformationLine className="MaterialityInformationLine" summary={summary}>
				<span className="materiality-information-line__label aresLabelName">{labels.currentPeriodPm}</span>
				<span className="materiality-information-line__percentage">
					{percentageValue >= 0 && !isNil(pmAmount) ? (
						<NumberFormat value={percentageValue} renderText={(value) => value} suffix="%" />
					) : (
						<>{'- %'}</>
					)}
				</span>
				<span className="materiality-information-line__value aresLabelValue">
					(
					{percentageValue >= 0 && !isNil(pmAmount) ? (
						<NumberFormat value={pmAmount} renderText={(value) => value} />
					) : (
						<>{'-'}</>
					)}
					)
				</span>
				{!isNullOrUndefined(basisAmount) && !isNullOrUndefined(maximumRange) && pmAmount > bufferRange ? (
					<section className="StyledInvalidPMAmount" src={actionIcInfoOutline24px}>
						<Icon src={actionIcInfoOutline24px} />
						<span className="pm-ammount-invalid-text">{labels.pmValidationMessage}</span>
					</section>
				) : (
					''
				)}
			</MaterialityInformationLine>
		);
	},
	PMBasisPercentage: ({basisAmount, pmAmount, summary, ...props}) => {
		let percentageValue;
		if (!isNil(pmAmount) && basisAmount) {
			percentageValue = round((pmAmount / basisAmount) * 100, 1);
		}

		return (
			<MaterialityInformationLine className="MaterialityInformationLine" summary={summary}>
				<span className="materiality-information-line__label aresLabelName">{labels.pmBasisPercentage}:</span>
				<span className="materiality-information-line__percentage">
					{!isNil(pmAmount) && percentageValue >= 0 ? (
						<NumberFormat value={percentageValue} renderText={(value) => value} suffix="%" />
					) : (
						<> {'- %'}</>
					)}
				</span>
			</MaterialityInformationLine>
		);
	},
	PMRationale: ({
		rationale,
		pmAmount,
		showRationaleOnLow,
		showRationaleOnHigh,
		minimumRange,
		maximumRange,
		suggestedPMBracket,
		summary,
		...props
	}) => {
		const showRationale =
			(!isNullOrUndefined(pmAmount) && showRationaleOnLow) ||
			showRationaleOnHigh ||
			(!isNullOrUndefined(pmAmount) && !isNullOrUndefined(rationale) && isNullOrUndefined(suggestedPMBracket));

		return showRationale ? (
			<MaterialityInformationLine className="MaterialityInformationLine" summary={summary}>
				<span className="materiality-information-line__label aresLabelName">{labels.rationaleLabel}:</span>
				{rationale && <span className="materiality-information-line__value aresLabelValue">{rationale}</span>}
				{isNullOrEmpty(rationale) && <section className="NoResponseLabel">{labels.noResponseAvailable}</section>}
			</MaterialityInformationLine>
		) : null;
	},
	SuggestedSadRange: ({pmAmount, optionSelected, summary, decimalsAmmount, sadRoundingPercentage}) => {
		const sadMaxLessThanFivePercentage =
			!isNullOrUndefined(optionSelected.sadMaximumRange) && optionSelected.sadMaximumRange < sadRoundingPercentage
				? optionSelected.pmAmount * sadRoundingPercentage
				: null;

		if (pmAmount === null || pmAmount === undefined || optionSelected.sadMinimumRange === null) {
			return null;
		} else if (!isNullOrUndefined(sadMaxLessThanFivePercentage)) {
			return (
				<MaterialityInformationLine className="MaterialityInformationLine">
					<span className={summary === false ? 'none' : 'summary_label'}>{labels.sugestedSAD}</span>
					<span
						className={
							summary === false
								? 'materiality-information-line__percentage'
								: 'materiality-information-line__percentage_summary'
						}
					>
						{labels.lessthan} {sadRoundingPercentage * 100}%{' '}
					</span>

					<span className={summary === false ? 'materiality-information-line__value aresLabelValue' : 'none'}>
						({labels.lessthan}{' '}
						<NumberFormat value={roundSadValue(sadMaxLessThanFivePercentage, 0)} renderText={(value) => value} />)
					</span>
				</MaterialityInformationLine>
			);
		} else {
			let minn = optionSelected.sadMinimumRange * 100;
			let maxx = optionSelected.sadMaximumRange * 100;

			return minn * 100 === maxx * 100 ? (
				<MaterialityInformationLine className="MaterialityInformationLine">
					<span className={summary === false ? 'none' : 'summary_label'}>{labels.sugestedSAD}</span>
					<span
						className={
							summary === false
								? 'materiality-information-line__percentage'
								: 'materiality-information-line__percentage_summary'
						}
					>
						<NumberFormat value={roundSadValue(maxx, decimalsAmmount)} renderText={(value) => value} suffix="%" />
					</span>

					<span className={summary === false ? 'materiality-information-line__value aresLabelValue' : 'none'}>
						(<NumberFormat value={roundSadValue((pmAmount * maxx) / 100, 0)} renderText={(value) => value} />)
					</span>
				</MaterialityInformationLine>
			) : (
				<MaterialityInformationLine className="MaterialityInformationLine">
					<span className={summary === false ? 'none' : 'summary_label'}>{labels.sugestedSAD}</span>
					<span
						className={
							summary === false
								? 'materiality-information-line__percentage'
								: 'materiality-information-line__percentage_summary'
						}
					>
						<NumberFormat value={roundSadValue(minn, decimalsAmmount)} renderText={(value) => value} suffix="%" />
					</span>
					<span className={summary === false ? 'materiality-information-line__value aresLabelValue' : 'none'}>
						(<NumberFormat value={roundSadValue((pmAmount * minn) / 100, 0)} renderText={(value) => value} />)
					</span>
					<span className="RangeSeparator"> to </span>
					<span
						className={
							summary === false
								? 'materiality-information-line__percentage'
								: 'materiality-information-line__percentage_summary'
						}
					>
						<NumberFormat value={roundSadValue(maxx, decimalsAmmount)} renderText={(value) => value} suffix="%" />
					</span>

					<span className={summary === false ? 'materiality-information-line__value aresLabelValue' : 'none'}>
						(<NumberFormat value={roundSadValue((pmAmount * maxx) / 100, 0)} renderText={(value) => value} />)
					</span>
				</MaterialityInformationLine>
			);
		}
	}
};

MaterialityDisplay.MeasurementBasis.displayName = 'Measurement Basis';
MaterialityDisplay.BasisAdjustedAmount.displayName = 'Basis Adjusted Amount';
MaterialityDisplay.EndOfRange.displayName = 'End Of Range';
MaterialityDisplay.LowEndOfRange.displayName = 'Low End Of Range';
MaterialityDisplay.HighEndOfRange.displayName = 'High End Of Range';
MaterialityDisplay.Range.displayName = 'Range';
MaterialityDisplay.PriorPeriodPM.displayName = 'Prior Period PM';
MaterialityDisplay.CurrentPeriodPM.displayName = 'Current Period PM';
MaterialityDisplay.SuggestedSadRange.displayName = 'Suggested SAD Percentage';

export default MaterialityDisplay;

export function TEMaterialityDisplay(currentMateriality, roundingBufferPercentage, currentTERationale) {
	if (isNullOrUndefined(currentMateriality?.teAmount)) {
		///If TE Amount has no value
		return false;
	} else if (
		isNullOrUndefined(currentMateriality?.suggestedTEPercent) &&
		!isNullOrUndefined(currentMateriality?.teAmount) &&
		!isNullOrUndefined(currentTERationale) &&
		currentTERationale !== ''
	) {
		//Restore
		return true;
	} else if (
		!isNullOrUndefined(currentMateriality?.suggestedTEPercent) &&
		!isNullOrUndefined(currentMateriality?.pmAmount)
	) {
		const suggestedTEPercent = Math.abs(currentMateriality?.suggestedTEPercent) / 100;
		const teRoundingBufferPercentage = (roundingBufferPercentage ?? 5) / 100;

		const suggestedBufferLowRange = calculateBufferedValue(
			suggestedTEPercent,
			currentMateriality?.pmAmount,
			'low',
			teRoundingBufferPercentage
		);
		const suggestedBufferHighRange = calculateBufferedValue(
			suggestedTEPercent,
			currentMateriality?.pmAmount,
			'high',
			teRoundingBufferPercentage
		);

		return (
			currentMateriality?.teAmount > suggestedBufferHighRange || currentMateriality?.teAmount < suggestedBufferLowRange
		);
	} else {
		//Default to false
		return false;
	}
}

export function CheckSADRationale(materiality, sadAmountToSave, sadRoundingBufferPercentage, currentSADRationale) {
	let isUnderSuggestedRange;

	if (!isNullOrUndefined(materiality?.pmAmount) && !isNullOrUndefined(materiality?.sadMinimumRange)) {
		const suggestedSADLowerBuffer = calculateBufferedValue(
			materiality?.sadMinimumRange,
			materiality?.pmAmount,
			'low',
			sadRoundingBufferPercentage
		);
		isUnderSuggestedRange = Math.round(sadAmountToSave) < suggestedSADLowerBuffer;
	}

	if (sadAmountToSave === '' || isNullOrUndefined(materiality?.sadNominalAmount)) {
		return false;
	}

	const sadPercenCheck = !isNullOrUndefined(materiality?.sadMaximumRange);

	if (sadPercenCheck && isUnderSuggestedRange) {
		return true;
	}

	if (
		!sadPercenCheck &&
		!isNullOrUndefined(sadAmountToSave) &&
		!isNullOrUndefined(currentSADRationale) &&
		currentSADRationale !== ''
	) {
		return true;
	}

	return false;
}

const MaterialityInformationLine = styled.section`
	font-size: var(--px-14);
	margin-bottom: var(--px-8);
	color: var(--neutrals-900);
	.aresLabelNameValueWrapper {
		margin-bottom: var(--px-8);
		.aresLabelName {
			font-size: var(--px-14) !important;
			font-weight: 300 !important;
			line-height: var(--px-20) !important;
			color: var(--neutrals-900);
		}
		.aresLabelValue {
			font-size: var(--px-14) !important;
			font-weight: 400 !important;
			line-height: var(--px-20) !important;
			color: var(--neutrals-900);
		}
	}

	.SuggestedRangeWrapper {
		margin-left: var(--px-3);
		.materiality-information-line__percentage {
			margin-left: 0;
		}
	}

	${(props) =>
		props.summary
			? `
        .materiality-information-line__label {
            --display: inline-block;
            font-weight: bold;
        }

		.materiality-information-line__percentage {
			display: inline-block;
			margin-left: var(--px-5);
		}
		.materiality-information-line__value {
			display: inline-block;
			margin-left: var(--px-5);
		}
		.RangeSeparator {
			& + .materiality-information-line__percentage {
				margin-left: 0;
			}
		}
	`
			: `
		.materiality-information-line__label {
			display: inline-block;
			font-weight: 300;
			line-height: var(--px-20);
			color: var(--neutrals-900);
			&.materialityInformationLineWrap {
				padding-right: var(--px-5);
			}
			&.materialityInformationLinelabelWrap {
				display: inline;
			}
			&.start-space {
				white-space: pre;
			}
		}

        .materiality-information-line__percentage_summary{
			margin-right: var(--px-5);
		}

		.summary_label{
			display: inline-block;
			margin-right: var(--px-5);
			font-weight: bold;
		}

        .materiality-information-line__percentage {
            display: inline-block;
            margin-left: var(--px-5);
            font-weight: 400;
            color: var(--teal-600);
			line-height: var(--px-20);
        }
        .materiality-information-line__value {
            display: inline-block;
            margin-left: var(--px-5);
            font-weight: 400;
			line-height: var(--px-20);
			color: var(--neutrals-900);
        }
        .RangeSeparator {
            & + .materiality-information-line__percentage {
                margin-left: 0;
            }
        }`}
	.materiality-information-line__percentage {
		color: var(--teal-600);
		font-size: var(--px-14) !important;
		font-weight: 400 !important;
	}
	.StyledInvalidPMAmount {
		display: flex;
		align-items: center;
		.pm-ammount-invalid-text {
			margin-left: var(--px-4);
		}
		.motif-icon {
			display: flex;
			align-items: center;
		}
	}
`;
